<template>
  <div class="lme-content d-flex flex-column gap-4">
    <v-form class="d-flex flex-column gap-6" v-if="lmeAvailable">
      <div class="d-flex flex-column gap-2">
        <p class="field-title ma-0">Medicamentos solicitados</p>
        <box-node v-for="(indicatedTreatment, i) in indicatedTreatmentsWithLme" :key="i">
          <div class="d-flex flex-column gap-2">
            <span>{{ indicatedTreatment.treatment.medicine.name }}</span>
            <span class="font-size-14">Informe a quantidade para cada mês</span>
            <div class="grid-6-columns gap-4">
              <simple-text-input-component
                v-for="i in 6"
                :key="i"
                :label="`${i}º mês`"
                v-model="form.fields.prescriptions[indicatedTreatment.treatment.id][i - 1]"
              />
            </div>
          </div>
        </box-node>
      </div>

      <div class="d-flex flex-column">
        <p class="field-title">Medicamentos anteriores</p>
        <v-text-field
          :value="previousTreatmentsText"
          filled
          dense
          height="48"
          background-color="surfaceContainerLow"
          hide-details
          disabled
        />
      </div>

      <div class="d-flex flex-column">
        <p class="field-title">CID</p>
        <v-text-field
          :value="hypothesis.cid"
          filled
          dense
          height="48"
          background-color="surfaceContainerLow"
          hide-details
          disabled
        />
      </div>

      <div class="grid-3-columns gap-4">
        <div class="d-flex flex-column">
          <p class="field-title">Local</p>
          <v-select
            label="Selecione o local"
            filled
            dense
            height="48"
            background-color="surfaceContainerLow"
            :items="attendance.physicianCnes"
            v-model="form.fields.location"
            :itemText="(i) => `${i.code} - ${i.name}`"
            itemValue="id"
            hide-details
          />
        </div>
        <div class="d-flex flex-column">
          <p class="field-title">Nome da mãe do paciente</p>
          <v-text-field
            label="Nome"
            filled
            dense
            height="48"
            background-color="surfaceContainerLow"
            v-model="form.fields.motherName"
            hide-details
            readonly
          ></v-text-field>
        </div>
        <div class="d-flex flex-column">
          <p class="field-title">Cor da pele do paciente</p>
          <v-select
            label="Selecione a cor da pele"
            filled
            dense
            height="48"
            background-color="surfaceContainerLow"
            v-model="form.fields.skinColor"
            :items="skinColors"
            hide-details
          />
        </div>
      </div>

      <div class="grid-3-columns gap-4">
        <div class="d-flex flex-column">
          <p class="field-title">Atestado de incapacidade?</p>
          <v-radio-group v-model="form.fields.attested" row mandatory @change="handleRadioGroup" hide-details>
            <v-radio label="Sim" value="sim"></v-radio>
            <v-radio label="Não" value="nao"></v-radio>
          </v-radio-group>
        </div>
        <div class="d-flex flex-column" v-if="form.fields.attested === 'sim'">
          <p class="field-title">Responsável pelo atestado</p>
          <v-text-field
            label="Nome"
            filled
            dense
            height="48"
            background-color="surfaceContainerLow"
            v-model="form.fields.responsibleForAttestation"
            hide-details
          ></v-text-field>
        </div>
      </div>
      <div class="d-flex flex-column">
        <p class="field-title">Imprimir com data atual?</p>
        <v-radio-group v-model="form.fields.printWithCurrentDate" row mandatory hide-details>
          <v-radio label="Sim" value="SIM"></v-radio>
          <v-radio label="Não" value="NAO"></v-radio>
        </v-radio-group>
      </div>

      <div class="d-flex flex-column gap-1">
        <div class="d-flex flex-row gap-1">
          <v-btn text height="40" color="primary" class="new-btn align-self-start" @click="getGeneratedAnamnesis">
            Gerar nova Anamnese
          </v-btn>

          <v-btn text height="40" color="primary" class="new-btn align-self-start" @click="clearAnamnesis">
            Limpar anamnese
          </v-btn>
        </div>

        <v-textarea
          filled
          label="Anamnese"
          v-model="form.fields.anamnesis"
          auto-grow
          counter
          maxlength="5000"
          class="anamnesis-textarea"
          hide-details
        >
        </v-textarea>
      </div>
    </v-form>
    <div v-else>
      <v-textarea
        dense
        filled
        label="texto da LME"
        placeholder="Aguardando resposta das perguntas obrigatórias"
        persistent-placeholder
        disabled
        rows="2"
      />
    </div>
    <div class="d-flex flex-row gap-4">
      <download-frame
        :url="printLmeUrl"
        alwaysForceDownload
        :disabled="!lmeAvailable"
        remoteUrl
        :onBeforeDownload="saveLme"
      >
        <v-btn text height="40" color="primary" class="new-btn rounded-lg" :disabled="!lmeAvailable" outlined>
          Imprimir LME
        </v-btn>
      </download-frame>
      <download-frame :url="printEmptyLmeUrl" alwaysForceDownload>
        <v-btn text height="40" color="primary" class="new-btn"> Imprimir modelo vazio </v-btn>
      </download-frame>
    </div>
  </div>
</template>

<script>
import { Computed, Method, ObjectLiteral } from '@/_typedefs'
import DownloadFrame from '@/components/DownloadFrame.vue'
import BoxNode from '@/components/NewAttendanceForm/FormNodes/Box/BoxNode.vue'
import SimpleTextInputComponent from '@/components/NewAttendanceForm/Questions/Basic/TextInput/SimpleTextInput.component.vue'
import { PUBLIC_STORAGE_URL } from '@/constants/global'
import { SKIN_COLOR } from '@/constants/person'
import { isEmpty, startCase } from 'lodash'
import { PropOptions } from 'vue'
import { createNamespacedHelpers } from 'vuex'
import { generateAnamnesis } from './anamnesisGenerator'
import treatmentDisplayBehaviours from './treatmentDisplayBehaviour'

const {
  mapGetters: newAttendanceFormGetters,
  mapMutations: newAttendanceFormMutations,
  mapActions: newAttendanceFormActions,
} = createNamespacedHelpers('newAttendanceForm')

export default {
  components: { DownloadFrame, BoxNode, SimpleTextInputComponent },
  name: 'LmeNode',
  props: {
    /** @type {PropOptions<boolean>} */
    lmeAvailable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        fields: {
          /** @type {Record<number, number[]>} */
          prescriptions: {},
          location: null,
          motherName: null,
          skinColor: null,
          attested: null,
          responsibleForAttestation: null,
          printWithCurrentDate: null,
          printWithSignature: null,
          anamnesis: null,
        },
      },
      /** @type {ObjectLiteral[]} */
      indicatedTreatmentsWithLme: [],
      /** @type {string[]} */
      previousTreatments: [],
      /** @type {ObjectLiteral} */
      hypothesis: {},
      skinColors: SKIN_COLOR,
      publicStorageUrl: PUBLIC_STORAGE_URL,
      treatmentDisplayConfig: [],
      loaded: false,
      unwatch: null,
    }
  },
  computed: {
    .../** @type {{treatments: Computed<ObjectLiteral[]>, extraAnswers: Computed<ObjectLiteral>, questions: Computed<ObjectLiteral>, parameters: Computed<ObjectLiteral>, attendance: Computed<ObjectLiteral>, patientData: Computed<ObjectLiteral>}} */ (
      newAttendanceFormGetters([
        'treatments',
        'extraAnswers',
        'questions',
        'answers',
        'parameters',
        'attendance',
        'patientData',
      ])
    ),
    /** @type {Computed<string>} */
    indicatedTreatmentsText() {
      return Object.values(this.indicatedTreatmentsWithLme)
        .map(({ treatment }) => treatment.medicine.name)
        .join(', ')
    },
    /** @type {Computed<string>} */
    previousTreatmentsText() {
      return this.previousTreatments.join(', ')
    },
    /** @type {Computed<string?>} */
    formattedHypothesisCid() {
      if (!this.hypothesis?.cid) {
        return null
      }

      return this.hypothesis.cid.includes('.') ? this.hypothesis.cid : `${this.hypothesis.cid}.0`
    },
    /** @type {Computed<ObjectLiteral>} */
    lmeData() {
      return {
        patientMotherName: this.form.fields.motherName,
        patientSkinColor: this.form.fields.skinColor,
        medicines: Object.entries(this.form.fields.prescriptions).map(([treatmentId, prescriptions]) => ({
          treatmentId,
          name: this.indicatedTreatmentsWithLme[treatmentId].treatment.medicine.name,
          month1: prescriptions[0],
          month2: prescriptions[1],
          month3: prescriptions[2],
          month4: prescriptions[3],
          month5: prescriptions[4],
          month6: prescriptions[5],
        })),
        previousTreatments: this.previousTreatmentsText,
        attested: this.form.fields.attested,
        responsibleName: this.form.fields.responsibleForAttestation,
        location: this.form.fields.location,
        anamnesis: this.form.fields.anamnesis,
        printCurrentDate: this.form.fields.printWithCurrentDate,
        printSignature: this.form.fields.printWithSignature,
        cid: this.parameters['asthma:hypothesis'].label.cid,
      }
    },
    /** @type {Computed<string>} */
    printEmptyLmeUrl() {
      return `${this.publicStorageUrl}/lme/lme-vazio.pdf`
    },
    /** @type {Computed<string>} */
    printLmeUrl() {
      return `${this.$util.apiURL()}/print-lme/${this.attendance.id}`
    },
  },
  methods: {
    ...newAttendanceFormMutations(['updateExtraAnswer', 'updatePatientData']),
    ...newAttendanceFormActions(['saveAllAnswers']),
    /** @type {Method<[val: string]>} */
    handleRadioGroup(val) {
      if (val === 'NAO') {
        this.form.fields.responsibleForAttestation = null
      }
    },
    getIndicatedTreatmentDosage(treatment) {
      let indicatedTreatmentDosage = treatment.dosages.find((dosage) => {
        const indicatedTreatment = this.extraAnswers['indicated-treatments'][treatment.id] ?? {}
        return (
          dosage.id === indicatedTreatment.id &&
          dosage.treatmentDosageLme.showLmeInAttendance &&
          dosage.treatmentDosageLme.cids.map((cid) => cid.code).includes(this.formattedHypothesisCid)
        )
      })

      if (!indicatedTreatmentDosage) {
        indicatedTreatmentDosage = treatment.dosages.find(
          (dosage) =>
            dosage.treatmentDosageLme.showLmeInAttendance &&
            dosage.treatmentDosageLme.cids.map((cid) => cid.code).includes(this.formattedHypothesisCid),
        )
      }

      return indicatedTreatmentDosage
    },
    getGeneratedAnamnesis() {
      this.form.fields.anamnesis = generateAnamnesis()
    },
    clearAnamnesis() {
      this.form.fields.anamnesis = ''
    },
    async saveLme() {
      this.updateExtraAnswer({ name: 'lme-data', value: this.lmeData })
      await this.saveAllAnswers()
    },
    updateFromState() {
      this.hypothesis = this.parameters['asthma:hypothesis']?.label ?? {}

      const indicatedTreatmentIds = Object.keys(this.extraAnswers['indicated-treatments'] ?? {})
      this.indicatedTreatmentsWithLme = Object.fromEntries(
        this.treatments
          .filter((treatment) => {
            if (!indicatedTreatmentIds.includes(String(treatment.id))) {
              return false
            }

            const indicatedTreatmentDosage = this.getIndicatedTreatmentDosage(treatment)
            return !!indicatedTreatmentDosage
          })
          .map((treatment) => {
            const foundConfig = this.treatmentDisplayConfig.find(
              (config) => config.medicineName === treatment.medicine.name,
            )

            if (!foundConfig || !(foundConfig.behaviour in treatmentDisplayBehaviours)) {
              return treatment
            }
            return treatmentDisplayBehaviours[foundConfig.behaviour](treatment)
          })
          .map((treatment) => {
            const indicatedTreatmentDosage = this.getIndicatedTreatmentDosage(treatment)
            return [treatment.id, { treatment, selectedDosage: indicatedTreatmentDosage }]
          }),
      )

      this.form.fields.prescriptions = Object.values(this.indicatedTreatmentsWithLme).reduce((acc, item) => {
        if (item.treatment.id in acc) {
          return acc
        }
        return {
          ...acc,
          [item.treatment.id]: [
            item.selectedDosage.treatmentDosageLme.firstPrescriptionMonth1,
            item.selectedDosage.treatmentDosageLme.firstPrescriptionMonth2,
            item.selectedDosage.treatmentDosageLme.firstPrescriptionMonth3,
            item.selectedDosage.treatmentDosageLme.firstPrescriptionMonth4,
            item.selectedDosage.treatmentDosageLme.firstPrescriptionMonth5,
            item.selectedDosage.treatmentDosageLme.firstPrescriptionMonth6,
          ],
        }
      }, this.form.fields.prescriptions)

      const previousTreatmentsQuestionKeys = Object.entries(this.questions)
        .filter(([_key, question]) => question.kind === 'asthma:previous-treatment')
        .map(([key, _question]) => key)

      this.previousTreatments = Object.entries(this.answers)
        .filter(([key, _answer]) => previousTreatmentsQuestionKeys.includes(key))
        .flatMap(([_key, answer]) =>
          Object.entries(answer).map(([key, item]) =>
            key === 'other' ? item.customMedicine : startCase(key).replaceAll(' ', ' + '),
          ),
        )
    },
    async getTreatmentConfigParameter() {
      const { value } = await this.$api.getParameter('TREATMENT_LME_DISPLAY_CONFIG')
      this.treatmentDisplayConfig = value
    },
    verifyPatientAge() {
      const { birthday } = this.attendance.patient

      if (this.$moment().diff(birthday, 'years') >= 18) {
        this.form.fields.attested = 'nao'
      }
    },
  },
  watch: {
    lmeAvailable: {
      handler(val) {
        if (val && !this.loaded) {
          this.getGeneratedAnamnesis()
        }
      },
    },
  },
  async created() {
    this.verifyPatientAge()
    await this.getTreatmentConfigParameter()
    const previousLmeData = this.extraAnswers['lme-data'] ?? { patientMotherName: this.attendance.patient.motherName }
    if (!isEmpty(previousLmeData)) {
      this.form.fields = {
        anamnesis: previousLmeData.anamnesis,
        location: previousLmeData.location,
        motherName: previousLmeData.patientMotherName,
        attested: previousLmeData.attested,
        printWithCurrentDate: previousLmeData.printCurrentDate,
        printWithSignature: previousLmeData.printSignature,
        responsibleForAttestation: previousLmeData.responsibleName,
        skinColor: previousLmeData.patientSkinColor,
        prescriptions: Object.fromEntries(
          previousLmeData.medicines?.map((medicine) => [
            medicine.treatmentId,
            [medicine.month1, medicine.month2, medicine.month3, medicine.month4, medicine.month5, medicine.month6],
          ]) ?? [],
        ),
      }
      if (previousLmeData.anamnesis) {
        this.loaded = true
      }
    }

    if (!this.form.fields.motherName) {
      const cpfVerification = this.$api.verifyCPF(this.attendance.patient.cpf)
      this.form.fields.motherName = cpfVerification?.verifiedUser.motherName
    }

    this.updateFromState()

    this.unwatch = this.$store.subscribe((mutation) => {
      if (
        ![
          'newAttendanceForm/updateAnswer',
          'newAttendanceForm/updateParameter',
          'newAttendanceForm/updateExtraAnswer',
          'newAttendanceForm/updatePatientData',
        ].includes(mutation.type)
      ) {
        return
      }

      this.updateFromState()
    })

    if (this.lmeAvailable && !this.loaded) {
      this.getGeneratedAnamnesis()
    }
  },
  beforeDestroy() {
    this.unwatch?.()
  },
}
</script>

<style scoped>
.v-text-field :deep(.v-input__control > .v-input__slot) {
  min-height: auto !important;
}
.theme--light.v-btn.v-btn--outlined.v-btn--text {
  border-color: inherit;
}

.lme-content {
  max-width: 1200px;
}

/* .flex-row {
  display: flex;
  gap: 24px;
} */

.w-384 {
  width: 100%;
  max-width: 384px;
}

.field-title {
  color: var(--v-onSurface-base);
  font-weight: 400;
  font-size: 14px;
  letter-spacing: 0.25px;
  margin-bottom: 8px;
}
.signature-legend {
  color: var(--v-neutral-lighten50);
  font-weight: 400;
  font-size: 12px;
}

.anamnesis-textarea :deep(textarea) {
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.25px;
  color: var(--v-onSurface-base);
}

.checklist {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border: 1px solid var(--v-neutral-lighten90);
  border-width: 1px 0 0 0;
  /* border-top: 1px solid var(--v-neutral-lighten90); */
}
.checklist > div > h3 {
  color: var(--v-neutral-lighten17);
  font-size: 16px;
  letter-spacing: 0.15px;
  font-weight: 500;
  margin-bottom: 4px;
}

.checklist > div > p {
  color: var(--v-neutral-lighten17);
  font-size: 14px;
  letter-spacing: 0.25px;
  font-weight: 400;
  margin: 0;
}

.v-btn {
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
}

.v-input--radio-group :deep(.v-input--radio-group__input) {
  gap: 32px;
}
.v-input--radio-group :deep(label) {
  color: var(--v-neutral-lighten17);
  letter-spacing: 0.25px;
  font-size: 14px;
  font-weight: 400;
}

.v-text-field :deep(input),
.v-select :deep(.v-select__selection) {
  font-size: 14px;
  letter-spacing: 0.25px;
  font-weight: 400;
}
.v-select :deep(.v-label:not(.v-label--active)),
.v-text-field :deep(.v-label:not(.v-label--active)) {
  font-size: 14px;
}
</style>
